import {
  AsyncPipe,
  CurrencyPipe,
  DOCUMENT,
  NgFor,
  NgIf
} from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { MomentModule } from 'ngx-moment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  BehaviorSubject,
  Observable,
  Subject,
  merge,
  of,
  withLatestFrom
} from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  shareReplay,
  startWith,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { HireItem, Item, Product } from '../../../models';
import { ItemAttribute } from '../../../models/item';
import { AddEstimatedOffHireDialogComponent } from '../../dialogs/add-estimated-off-hire-dialog/add-estimated-off-hire-dialog.component';
import { AddItemDialogComponent } from '../../dialogs/add-item-dialog/add-item-dialog.component';
import { AddQuantifiableItemDialogComponent } from '../../dialogs/add-quantifiable-item-dialog/add-quantifiable-item-dialog.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { EditOnHireDialogComponent } from '../../dialogs/edit-on-hire-dialog/edit-on-hire-dialog.component';
import { HireItemPriceUpdateService } from '../../dialogs/hire-price-structure-dialog/hire-item-price-update.service';
import { HirePriceStructureDialogComponent } from '../../dialogs/hire-price-structure-dialog/hire-price-structure-dialog.component';
import { HIREABLE_PRICE_UPDATE_IJ } from '../../dialogs/hire-price-structure-dialog/hireable-price-update';
import { ItemDialogComponent } from '../../dialogs/item-dialog/item-dialog.component';
import { OffHireDialogComponent } from '../../dialogs/off-hire-dialog/off-hire-dialog.component';
import { TestIdDirective } from '../../directives/test-id.directive';
import { extractQuantity } from '../../helpers/extractQuantity';
import {
  PromptEvent,
  TrackedAttributesPromptComponent
} from '../../item-attributes/tracked-attributes-prompt/tracked-attributes-prompt.component';
import { ItemSearchComponent } from '../../item-search/item-search.component';
import { LocationNameFromIdPipe } from '../../pipes/location-name-from-id.pipe';
import { PriceEstimateBreakdownPipe } from '../../pipes/price-estimate-breakdown.pipe';
import { UserIdToNamePipe } from '../../pipes/user-id-to-name.pipe';
import { ConfigService } from '../../services/config.service';
import { HireItemService } from '../../services/hire-item.service';
import { ItemAttributesService } from '../../services/item-attributes.service';
import { ItemService } from '../../services/item.service';
import { LinkedProductsService } from '../../services/linked-products.service';
import { ConflictCountComponent } from '../conflict-count/conflict-count.component';
import { AddHireItemAttachmentComponent } from './add-hire-item-attachment/add-hire-item-attachment.component';
import { HireItemAttachmentListComponent } from './hire-item-attachment-list/hire-item-attachment-list.component';
import { HireItemListStoreService } from './hire-item-list-store.service';
import { ProductNameCellComponent } from './product-name-cell/product-name-cell.component';
import { VirtualProductDialogComponent } from './virtual-product-dialog/virtual-product-dialog.component';

@Component({
  selector: 'app-hire-item-list-v2',
  templateUrl: './hire-item-list-v2.component.html',
  providers: [
    ItemService,
    ItemAttributesService,
    HireItemPriceUpdateService,
    LinkedProductsService,
    {
      provide: HIREABLE_PRICE_UPDATE_IJ,
      useClass: HireItemPriceUpdateService
    }
  ],
  standalone: true,
  imports: [
    MatCard,
    FlexModule,
    MatCardTitle,
    MatIconButton,
    MatIcon,
    MatCardContent,
    NgIf,
    NgxSkeletonLoaderModule,
    MatCheckbox,
    TestIdDirective,
    ReactiveFormsModule,
    FormsModule,
    ConflictCountComponent,
    TrackedAttributesPromptComponent,
    HireItemAttachmentListComponent,
    MatButton,
    ItemSearchComponent,
    AsyncPipe,
    CurrencyPipe,
    LocationNameFromIdPipe,
    UserIdToNamePipe,
    PriceEstimateBreakdownPipe,
    MomentModule,
    ProductNameCellComponent
  ],
  styles: [
    `
      :host {
        width: 100%;
      }
    `
  ]
})
export class HireItemListV2Component implements OnDestroy {
  @Output() refetchDocket = new EventEmitter();
  unsubscribe$ = new Subject<void>();

  @Input() set docketId(value: number) {
    this._docketId = value;
    this.store.setDocketAndLoad(value);
  }

  @Input() customerId: number;
  @Input() defaultCurrency = 'GBP';
  @Input() currency = 'GBP';
  @Input() currencyRate = 1;
  @Input() docketClosed = false;
  @ViewChild(ItemSearchComponent, { static: false })
  itemSearch: ItemSearchComponent;
  @Input() previewMode = false;

  @Output() itemsChanged = this.store.hireItems$;
  @Output() openPaymentDialog = new EventEmitter();

  private _docketId: number;
  hireItems$ = this.store.hireItemsWithExtras$;
  onHireCount$ = this.store.onHireCount$;
  shouldShowOffHireToggle$ = this.store.onHireCount$.pipe(
    map((count) => count > 0 && !this.previewMode)
  );
  trackedAttributes: {
    [id: number]: Observable<{
      event: PromptEvent;
      attributes: ItemAttribute[];
    }>;
  } = {};
  totalHireItemsCount$ = this.store.totalHireItemCount$;
  loading$ = this.store.loading$;
  selectedMap: { [itemId: number]: boolean } = {};

  private selectionChangedSource = new Subject<void>();
  allSelected$ = merge(
    this.onHireCount$,
    this.selectionChangedSource.asObservable()
  ).pipe(
    withLatestFrom(this.onHireCount$),
    map(([_, count]) => this.checkIfAllSelected(count))
  );

  selectedCount$ = this.selectionChangedSource.pipe(
    map(() => {
      return Array.from(Object.values(this.selectedMap)).filter(
        (value) => value
      ).length;
    }),
    startWith(0)
  );

  disableActions = new BehaviorSubject<boolean>(false);
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;

  constructor(
    private store: HireItemListStoreService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private currencyPipe: CurrencyPipe,
    private hireItemService: HireItemService,
    @Inject(DOCUMENT) private document: Document,
    private dialog: MatDialog,
    private configService: ConfigService,
    private vcr: ViewContainerRef,
    private itemService: ItemService,
    private attributesService: ItemAttributesService
  ) {
    if (this.route.snapshot.queryParams['addItemId']) {
      this.addItemFromQueryParams();
    }
    if (this.route.snapshot.queryParams['createdFromBooking']) {
      this.getAllAttributes();
    }
  }

  private getAllAttributes() {
    this.hireItems$
      .pipe(
        filter((items) => items.length > 0),
        take(1),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((hireItems) => {
        this.updateTrackedAttributes(hireItems, 'on_hire');
        this.router.navigate([], {
          queryParams: {
            createdFromBooking: undefined
          },
          queryParamsHandling: 'merge'
        });
      });
  }

  private addItemFromQueryParams() {
    this.itemService
      .get(this.route.snapshot.queryParams['addItemId'], ['product'])
      .pipe(
        mergeMap((item) => this.saveItem(item, item.product)),
        take(1),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        complete: () => {
          this.router.navigate([], {
            queryParams: {
              addItemId: undefined
            },
            queryParamsHandling: 'merge'
          });
        }
      });
  }

  private checkIfAllSelected(available: number) {
    const selected = Array.from(Object.values(this.selectedMap)).filter(
      (value) => value
    );
    return selected.length === available;
  }

  toggleSelectAll(checked: boolean) {
    this.hireItems$
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((items) => {
        items
          .filter((item) => checked === false || item.off_hire === null)
          .forEach((item) => {
            this.selectedMap[item.id] = checked;
          });
      });
    this.selectionChangedSource.next();
  }

  onSelectionChanged(id: number, selected: boolean) {
    this.selectedMap[id] = selected;
    this.selectionChangedSource.next();
  }

  showEditOnHireDialog(item) {
    let data = {
      data: {
        item: item,
        currencyRate: this.currencyRate,
        currency: this.currency,
        customerId: this.customerId
      },
      maxHeight: '90vh',
      closeOnNavigation: true,
      disableClose: true,
      viewContainerRef: this.vcr
    } as MatDialogConfig;
    this.dialog
      .open(EditOnHireDialogComponent, data)
      .afterClosed()
      .subscribe((updated) => {
        if (updated) {
          this.store.reloadHireItems();
        }
      });
  }

  openDeleteDialog(hireItem: HireItem) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent);
    confirmDialogRef.componentInstance.message = `Are you sure you want to delete ${hireItem.hireable_type === 'product' ? (hireItem.hireable as Product).name : (hireItem.hireable as Item).product.name} from the hire contract?\n\nDeleted hire items are also removed from this contracts delivery and collection jobs.`;
    confirmDialogRef.componentInstance.title = 'Delete Item';
    confirmDialogRef.componentInstance.type = 'delete';

    confirmDialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.disableActions.next(true);
        this.store.deleteHireItem(hireItem.id);
        this.disableActions.next(false);
      }
    });
  }

  reloadHireItems() {
    this.store.reloadHireItems();
  }

  copyHireItems() {
    this.hireItems$
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((hireItems) => {
        const hireItemsString = hireItems
          .filter((hireItem) => !hireItem.deleted)
          .map((hireItem) => {
            let offHire;
            if (hireItem.off_hire) {
              offHire = moment(hireItem.off_hire).format(this.dateTimeFormat);
            } else if (hireItem.estimated_off_hire) {
              offHire =
                moment(hireItem.estimated_off_hire).format(
                  this.dateTimeFormat
                ) + ' (Estimated)';
            } else {
              offHire = 'Present';
            }

            let charge;
            if (hireItem.charge === null) {
              charge = 'Charge Pending';
            } else {
              charge = this.currencyPipe.transform(
                hireItem.charge,
                this.defaultCurrency,
                'symbol',
                '1.2-2'
              );
            }

            return (
              `${moment(hireItem.on_hire).format(
                this.dateTimeFormat
              )} - ${offHire},` +
              ` ${hireItem.hireable_type === 'product' ? (hireItem.hireable as Product).name : (hireItem.hireable as Item).product.name} (${hireItem.item.code}), ${charge}`
            );
          });

        const el = this.document.createElement(
          'textarea'
        ) as HTMLTextAreaElement;
        el.value = hireItemsString.join('\n');
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        this.document.body.appendChild(el);
        el.select();
        this.document.execCommand('copy');
        this.document.body.removeChild(el);

        this.snackBar.open('Hire items copied', null, { duration: 2000 });
      });
  }

  offHireItems() {
    this.hireItems$
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        withLatestFrom(this.onHireCount$),
        mergeMap(([hireItems, onHirecount]) => {
          const items = hireItems.filter((item) => this.selectedMap[item.id]);
          const closingAction = items.length === onHirecount;
          const dialogData = {
            items,
            closingAction,
            docketId: this._docketId,
            customerId: this.customerId,
            currencyRate: this.currencyRate,
            currency: this.currency
          };
          return this.dialog
            .open(OffHireDialogComponent, {
              data: dialogData,
              closeOnNavigation: true,
              disableClose: true
            })
            .afterClosed()
            .pipe(
              tap((res) => {
                if (res) {
                  // update tracked attributes on successful off hire
                  this.updateTrackedAttributes(items, 'off_hire');
                  // deselect all selected items on successful off hire
                  this.toggleSelectAll(false);
                  if (closingAction) {
                    this.refetchDocket.emit();
                  }
                }
              })
            );
        })
      )
      .subscribe((closed) => {
        this.store.reloadHireItems();
      });
  }

  optionSelected(option: Product) {
    if (option.virtual) {
      this.showVirtualProductDialog(option);
      return;
    }
    if (option.quantifiable && option.items.length) {
      this.addQuantifiableItem(option);
      return;
    }
    if (option.items.length === 1) {
      if (this.isItemAvailable(option.items[0])) {
        this.saveItem(option.items[0], option)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe();
      }
      return;
    }
    if (option.items.length === 0) {
      this.showNoItemsMessage(option);
      return;
    }
    this.showSelectItemDialog(option);
  }

  showVirtualProductDialog(product: Product) {
    this.dialog
      .open(VirtualProductDialogComponent, {
        data: { product, docketId: this._docketId },
        width: '800px',
        maxHeight: '90vh'
      })
      .afterClosed()
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        filter((result) => result),
        tap((result) => result && this.reloadHireItems())
      )
      .subscribe((hireItem: HireItem) => {
        this.updateTrackedAttributes([hireItem], 'on_hire');
      });
  }

  openPriceSettings(item: HireItem) {
    this.dialog
      .open(HirePriceStructureDialogComponent, {
        data: {
          productId:
            item.hireable_type === 'product'
              ? item.hireable_id
              : (item.hireable as Item).product.id,
          customerId: this.customerId,
          parentId: item.docket_id,
          currencyRate: this.currencyRate,
          quantity: item.quantity,
          pricingType: item.pricing_type,
          hireableId: item.id,
          hireableType: 'hireItem',
          onHire: item.on_hire,
          offHire: item.off_hire,
          currency: this.currency,
          fixedPrice: item.pricing_type === 'fixed' ? item.charge : null
        },
        viewContainerRef: this.vcr
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.store.reloadHireItems();
      });
  }

  private showNoItemsMessage(product: Product) {
    this.snackBar
      .open(
        `${product.name} does not have any items, you must add at least one item before you can hire this product`,
        'View Product',
        { duration: 8000 }
      )
      .onAction()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.router.navigate(['product', product.id]);
      });
  }

  private isItemAvailable(item: Item) {
    const messages = [];
    if (item.status !== 'Available') {
      messages.push(`This item is currently on hire`);
    }
    if (item.deleted) {
      messages.push(`This item has been deleted`);
    }
    if (item.has_current_workshop_jobs) {
      messages.push(`This item has one or more incomplete workshop jobs`);
    }
    if (messages.length > 0) {
      this.snackBar
        .open(
          `${item.code} cannot be put on hire: ${messages.join(', ')}`,
          'View Item',
          {
            duration: 8000
          }
        )
        .onAction()
        .pipe(take(1), takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.dialog.open(ItemDialogComponent, {
            data: { item }
          });
        });
      return false;
    }
    return true;
  }

  private getLastOffHireEst() {
    return this.hireItems$.pipe(
      take(1),
      takeUntil(this.unsubscribe$),
      map((items) => {
        const futureEstiamtedOffHires = items.filter(
          (item) =>
            item.off_hire === null &&
            item.estimated_off_hire !== null &&
            moment(item.estimated_off_hire).isAfter(moment())
        );
        if (futureEstiamtedOffHires.length) {
          return futureEstiamtedOffHires.pop().estimated_off_hire;
        }
      })
    );
  }

  private saveItem(item: Item, product: Product, quantity = 1) {
    if (product.items) {
      delete product.items;
    }
    return this.getLastOffHireEst().pipe(
      takeUntil(this.unsubscribe$),
      mergeMap((estimatedOffHire) => {
        const hireItem = {
          item_id: item.id,
          hireable_id: item.id,
          hireable_type: 'item',
          docket_id: this._docketId,
          item_quantifiable: product.quantifiable,
          on_hire: new Date(),
          estimated_off_hire: estimatedOffHire,
          free_accessory: 0,
          quantity: quantity
        };
        return this.configService.currentLocation.pipe(
          take(1),
          takeUntil(this.unsubscribe$),
          mergeMap((location) =>
            this.hireItemService.create(this._docketId, {
              ...hireItem,
              location_out: location.id
            })
          )
        );
      }),
      mergeMap((hireItem) => {
        if (hireItem && hireItem.estimated_off_hire === null) {
          return this.dialog
            .open(AddEstimatedOffHireDialogComponent, {
              autoFocus: true,
              data: {
                hireItem: { ...hireItem, item }
              }
            })
            .afterClosed()
            .pipe(map(() => hireItem));
        }
        return of(hireItem);
      }),
      tap((hireItem) => this.updateTrackedAttributes([hireItem], 'on_hire')),
      tap(() => this.store.reloadHireItems())
    );
  }

  private addQuantifiableItem(product) {
    if (extractQuantity(this.itemSearch.filterControl.value)) {
      this.saveItem(product.items[0], product, this.getQuantity()).subscribe();
      return;
    }
    this.showQuantifiableDialog(product);
  }

  private showQuantifiableDialog(product: Product) {
    const quantifableModalData = {
      docketId: this._docketId,
      product: product,
      item: product.items[0],
      quantity: this.getQuantity()
    };
    this.getLastOffHireEst()
      .pipe(
        mergeMap((lastOffHireEst) => {
          return this.dialog
            .open(AddQuantifiableItemDialogComponent, {
              data: { ...quantifableModalData, lastOffHireEst }
            })
            .afterClosed();
        }),
        take(1),
        takeUntil(this.unsubscribe$),
        filter((result) => result),
        tap(() => this.store.reloadHireItems())
      )
      .subscribe();
  }

  private showSelectItemDialog(product: Product) {
    const config = {
      minWidth: '60vw',
      minHeight: 300,
      maxHeight: '80vh',
      data: {
        product
      }
    };

    this.dialog
      .open(AddItemDialogComponent, config)
      .afterClosed()
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        filter((result) => result && result.item),
        mergeMap((result) => this.saveItem(result.item, product))
      )
      .subscribe();
  }

  private getQuantity() {
    let quantity = 1;
    let quantityString = extractQuantity(this.itemSearch.filterControl.value);
    if (quantityString) {
      quantity = parseInt(quantityString, 10);
    }
    return quantity;
  }

  attach(hireItem: HireItem) {
    this.dialog.open(AddHireItemAttachmentComponent, {
      data: hireItem,
      viewContainerRef: this.vcr
    });
  }

  private updateTrackedAttributes(hireItems: HireItem[], event: PromptEvent) {
    const hireItemIdsAndItemIds = hireItems.flatMap(
      ({ hireable_type, hireable_id, id, children }) => {
        // if hireable type is item, return the hire item id
        if (hireable_type === 'item') {
          return [{ id, hireable_id }];
        }
        // if hireable type is product, return the children hire item ids
        if (hireable_type === 'product') {
          return children.map(({ id, hireable_id: childHireableId }) => ({
            id,
            hireable_id: childHireableId
          }));
        }
        return [];
      }
    );
    // query attributes for the item_id, assign to the trackedAttributes object with a key of the hireItem id
    hireItemIdsAndItemIds.forEach(({ id, hireable_id }) => {
      this.trackedAttributes[id] = this.attributesService
        .getAll(hireable_id)
        .pipe(
          map((attributes) =>
            attributes.length ? { event, attributes } : undefined
          ),
          shareReplay(1)
        );
    });
  }

  trackedAttributesAdded(id: number, $event: any) {
    delete this.trackedAttributes[id];
    if ($event.itemUpdated) {
      this.store.reloadHireItems();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
